import { API_BASE_URL } from './api';
import { authService } from './authService';

export const ClubJoinMode = {
  InviteOnly: 'InviteOnly',
  OpenLink: 'OpenLink',
} as const;
export type ClubJoinModeValue = typeof ClubJoinMode[keyof typeof ClubJoinMode];

export const ClubRole = {
  Member: 'Member',
  Admin: 'Admin',
  Owner: 'Owner',
} as const;
export type ClubRoleValue = typeof ClubRole[keyof typeof ClubRole];

export const ClubMemberType = {
  User: 'User',
  Group: 'Group',
} as const;
export type ClubMemberTypeValue = typeof ClubMemberType[keyof typeof ClubMemberType];

export interface DiveClub {
  clubId: string;
  name: string;
  description?: string | null;
  photoKey?: string | null;
  ownerUserId: string;
  joinMode: ClubJoinModeValue;
  shareCode: string;
  memberCount: number;
  /** Whether this club appears in club search results. Defaults to true. */
  searchable: boolean;
  /** Owner opt-in to show the club's name, description and size to signed-out visitors. Defaults to false. */
  showOnPublicPage?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClubMembership {
  clubId: string;
  memberId: string;
  memberType: ClubMemberTypeValue;
  role: ClubRoleValue;
  status: 'Pending' | 'Active';
  invitedByUserId: string;
  displayName: string;
  profilePhotoUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClubRequest {
  name: string;
  description?: string;
  joinMode?: ClubJoinModeValue;
}

export interface UpdateClubRequest {
  name?: string;
  description?: string;
  photoKey?: string;
  joinMode?: ClubJoinModeValue;
  searchable?: boolean;
  showOnPublicPage?: boolean;
}

const authJsonHeaders = () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() });

// Club photos live in the same public bucket as post/profile photos, just under whatever
// key prefix the upload endpoint that created them used — no new infra needed.
export const clubPhotoUrl = (photoKey: string): string =>
  `https://aidivecoach-post-photos.s3.us-east-2.amazonaws.com/${photoKey}`;

const throwWithBody = async (res: Response, fallback: string) => {
  const text = await res.text();
  throw new Error(text || fallback);
};

export const clubService = {
  searchClubs: async (query: string): Promise<DiveClub[]> => {
    if (query.trim().length < 2) return [];
    const res = await fetch(`${API_BASE_URL}/clubs/search?q=${encodeURIComponent(query.trim())}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to search clubs');
    return res.json();
  },

  createClub: async (request: CreateClubRequest): Promise<DiveClub> => {
    const res = await fetch(`${API_BASE_URL}/clubs`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to create club');
    return res.json();
  },

  getMyClubs: async (): Promise<DiveClub[]> => {
    const res = await fetch(`${API_BASE_URL}/clubs/mine`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load clubs');
    return res.json();
  },

  getClub: async (clubId: string): Promise<DiveClub> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load club');
    return res.json();
  },

  getMembers: async (clubId: string): Promise<ClubMembership[]> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/members`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load members');
    return res.json();
  },

  getPendingRequests: async (clubId: string): Promise<ClubMembership[]> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/requests`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load requests');
    return res.json();
  },

  updateClub: async (clubId: string, request: UpdateClubRequest): Promise<DiveClub> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}`, {
      method: 'PATCH',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to update club');
    return res.json();
  },

  deleteClub: async (clubId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to delete club');
  },

  inviteMember: async (clubId: string, memberId: string, memberType: ClubMemberTypeValue = ClubMemberType.User): Promise<ClubMembership> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/invite`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ memberId, memberType }),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to add member');
    return res.json();
  },

  joinByCode: async (shareCode: string): Promise<DiveClub> => {
    const res = await fetch(`${API_BASE_URL}/clubs/join-by-code`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ shareCode }),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to join club');
    return res.json();
  },

  requestJoin: async (clubId: string): Promise<ClubMembership> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/request-join`, {
      method: 'POST',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) return throwWithBody(res, 'Failed to request to join');
    return res.json();
  },

  approveRequest: async (clubId: string, memberId: string): Promise<ClubMembership> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/requests/${memberId}/approve`, {
      method: 'POST',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) return throwWithBody(res, 'Failed to approve request');
    return res.json();
  },

  declineOrCancelRequest: async (clubId: string, memberId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/requests/${memberId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to decline request');
  },

  updateMemberRole: async (clubId: string, memberId: string, role: ClubRoleValue): Promise<ClubMembership> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/members/${memberId}/role`, {
      method: 'PATCH',
      headers: authJsonHeaders(),
      body: JSON.stringify({ role }),
    });
    if (!res.ok) return throwWithBody(res, 'Failed to update role');
    return res.json();
  },

  removeMember: async (clubId: string, memberId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/clubs/${clubId}/members/${memberId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to remove member');
  },
};
